import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  School as SchoolIcon,
  ChildCare as PrePrimaryIcon,
  MenuBook as PrimaryIcon,
  Psychology as SecondaryIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AnalyticsIcon
} from '@mui/icons-material';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface SchoolLevelPerformance {
  dim_school__school_level_code: string;
  dim_school__school_level_name: string;
  schools_count: number;
  total_students_enrolled: number;
  total_students_present: number;
  total_teachers_assigned: number;
  total_teachers_present: number;
  avg_student_attendance_rate: number;
  avg_teacher_attendance_rate: number;
  high_performing_schools: number;
  medium_performing_schools: number;
  low_performing_schools: number;
}

interface DailyTrend {
  dim_date__date_key: string;
  dim_school__school_level_name: string;
  avg_student_rate: number;
  avg_teacher_rate: number;
  total_students: number;
  schools_reporting: number;
}

interface SchoolLevelAnalysisData {
  period: {
    start_date: string;
    end_date: string;
  };
  school_level_performance: SchoolLevelPerformance[];
  daily_trends: DailyTrend[];
}

const SchoolLevelAnalysis: React.FC = () => {
  const [analysisData, setAnalysisData] = useState<SchoolLevelAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [dateRange, setDateRange] = useState<number>(30);

  useEffect(() => {
    fetchSchoolLevelAnalysis();
  }, [selectedZone, dateRange]);

  const fetchSchoolLevelAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);

      const { attendanceAPI } = await import('../../services/attendanceApi');

      const response = await attendanceAPI.getSchoolLevelAnalysis({
        school_level: selectedZone || undefined,
        days_back: dateRange,
      });

      if (response.data.status === 'success') {
        setAnalysisData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load school level analysis');
      }
    } catch (err: any) {
      console.error('School level analysis error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load school level analysis');
    } finally {
      setLoading(false);
    }
  };

  const getSchoolLevelIcon = (levelName: string) => {
    if (levelName.toLowerCase().includes('pre-primary')) return <PrePrimaryIcon />;
    if (levelName.toLowerCase().includes('primary')) return <PrimaryIcon />;
    if (levelName.toLowerCase().includes('secondary')) return <SecondaryIcon />;
    return <SchoolIcon />;
  };

  const getPerformanceColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 95) return 'success';
    if (rate >= 85) return 'warning';
    return 'error';
  };

  // Prepare daily trends chart
  const getDailyTrendsChart = () => {
    if (!analysisData?.daily_trends) return null;

    const levels = Array.from(new Set(analysisData.daily_trends.map(t => t.dim_school__school_level_name)));
    const dates = Array.from(new Set(analysisData.daily_trends.map(t => t.dim_date__date_key))).sort();

    const datasets = levels.map((level, index) => {
      const levelData = analysisData.daily_trends.filter(t => t.dim_school__school_level_name === level);
      const colors = ['rgba(75, 192, 192, 1)', 'rgba(255, 99, 132, 1)', 'rgba(255, 205, 86, 1)'];

      return {
        label: `${level} - Student Attendance`,
        data: dates.map(date => {
          const dayData = levelData.find(d => d.dim_date__date_key === date);
          return dayData ? dayData.avg_student_rate : null;
        }),
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length].replace('1)', '0.2)'),
        tension: 0.1,
      };
    });

    return {
      labels: dates.map(date => new Date(date).toLocaleDateString()),
      datasets,
    };
  };

  // Prepare performance distribution chart
  const getPerformanceDistributionChart = () => {
    if (!analysisData?.school_level_performance) return null;

    const totalHigh = analysisData.school_level_performance.reduce((sum, level) => sum + level.high_performing_schools, 0);
    const totalMedium = analysisData.school_level_performance.reduce((sum, level) => sum + level.medium_performing_schools, 0);
    const totalLow = analysisData.school_level_performance.reduce((sum, level) => sum + level.low_performing_schools, 0);

    return {
      labels: ['High Performers (95%+)', 'Medium Performers (85-94%)', 'Need Support (<85%)'],
      datasets: [
        {
          data: [totalHigh, totalMedium, totalLow],
          backgroundColor: [
            'rgba(76, 175, 80, 0.6)',
            'rgba(255, 152, 0, 0.6)',
            'rgba(244, 67, 54, 0.6)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
      },
    },
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analysisData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No school level analysis data available
      </Alert>
    );
  }

  const trendsChart = getDailyTrendsChart();
  const distributionChart = getPerformanceDistributionChart();

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        <SchoolIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        School Level Analysis
      </Typography>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Attendance patterns by education level: Pre-Primary, Primary, and Secondary schools
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Zone</InputLabel>
                <Select
                  value={selectedZone}
                  label="Zone"
                  onChange={(e) => setSelectedZone(e.target.value)}
                >
                  <MenuItem value="">All Zones</MenuItem>
                  <MenuItem value="PEMBA">Pemba</MenuItem>
                  <MenuItem value="UNGUJA">Unguja</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select
                  value={dateRange}
                  label="Period"
                  onChange={(e) => setDateRange(Number(e.target.value))}
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={fetchSchoolLevelAnalysis}
                fullWidth
                startIcon={<AnalyticsIcon />}
              >
                Refresh Analysis
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* School Level Performance Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {analysisData.school_level_performance.map((level, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  {getSchoolLevelIcon(level.dim_school__school_level_name)}
                  <Box component="span" sx={{ ml: 1 }}>
                    {level.dim_school__school_level_name}
                  </Box>
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Schools</Typography>
                    <Typography variant="h5">{level.schools_count}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Students</Typography>
                    <Typography variant="h5">{level.total_students_enrolled?.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Student Attendance Rate</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={level.avg_student_attendance_rate}
                        sx={{ flexGrow: 1, mr: 1 }}
                        color={getPerformanceColor(level.avg_student_attendance_rate)}
                      />
                      <Chip
                        label={`${level.avg_student_attendance_rate.toFixed(1)}%`}
                        color={getPerformanceColor(level.avg_student_attendance_rate)}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Teacher Attendance Rate</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={level.avg_teacher_attendance_rate}
                        sx={{ flexGrow: 1, mr: 1 }}
                        color={getPerformanceColor(level.avg_teacher_attendance_rate)}
                      />
                      <Chip
                        label={`${level.avg_teacher_attendance_rate.toFixed(1)}%`}
                        color={getPerformanceColor(level.avg_teacher_attendance_rate)}
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>

                {/* Performance Distribution */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">School Performance Distribution</Typography>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="success.main">High</Typography>
                      <Typography variant="body2">{level.high_performing_schools}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="warning.main">Medium</Typography>
                      <Typography variant="body2">{level.medium_performing_schools}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="error.main">Low</Typography>
                      <Typography variant="body2">{level.low_performing_schools}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {trendsChart && (
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Daily Attendance Trends by School Level
                </Typography>
                <Line data={trendsChart} options={chartOptions} />
              </CardContent>
            </Card>
          </Grid>
        )}

        {distributionChart && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance Distribution
                </Typography>
                <Doughnut data={distributionChart} />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Detailed Performance Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed School Level Performance
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>School Level</TableCell>
                  <TableCell align="right">Schools</TableCell>
                  <TableCell align="right">Students</TableCell>
                  <TableCell align="right">Teachers</TableCell>
                  <TableCell align="right">Student Rate</TableCell>
                  <TableCell align="right">Teacher Rate</TableCell>
                  <TableCell align="right">High Performers</TableCell>
                  <TableCell align="right">Need Support</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analysisData.school_level_performance
                  .sort((a, b) => b.avg_student_attendance_rate - a.avg_student_attendance_rate)
                  .map((level, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getSchoolLevelIcon(level.dim_school__school_level_name)}
                          <Box component="span" sx={{ ml: 1 }}>
                            {level.dim_school__school_level_name}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">{level.schools_count}</TableCell>
                      <TableCell align="right">{level.total_students_enrolled?.toLocaleString()}</TableCell>
                      <TableCell align="right">{level.total_teachers_assigned?.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${level.avg_student_attendance_rate.toFixed(1)}%`}
                          color={getPerformanceColor(level.avg_student_attendance_rate)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${level.avg_teacher_attendance_rate.toFixed(1)}%`}
                          color={getPerformanceColor(level.avg_teacher_attendance_rate)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={level.high_performing_schools.toString()}
                          color="success"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={level.low_performing_schools.toString()}
                          color="error"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SchoolLevelAnalysis;